/**
 * WebRTC Connection Instrumentation
 *
 * This module patches the RTCPeerConnection constructor to maintain a live registry
 * of all active connections. This ensures reliable cleanup and prevents connection leaks.
 */

import { budgetManager } from './WebRTCBudgetManager'

// Debug flag - set to true to enable console output, false to disable
const DEBUG_WEBRTC_INSTRUMENTATION = false

// Debug helper function
function debugLog(...args: any[]) {
  if (DEBUG_WEBRTC_INSTRUMENTATION) {
    console.log(...args)
  }
}

// Global registry of active peer connections
const Live = new Set<RTCPeerConnection>()

/**
 * Instrument the RTCPeerConnection constructor to track all instances
 * Call this once early in your app's lifecycle
 */
export function instrumentPeerConnections() {
  if (typeof window === 'undefined') {
    debugLog(
      '[WebRTC Instrumentation] Window not available, skipping instrumentation'
    )
    return
  }

  if ((window as any).__pc_instrumented__) {
    debugLog('[WebRTC Instrumentation] Already instrumented, skipping')
    return
  }

  // Check if RTCPeerConnection is available and properly defined
  if (!window.RTCPeerConnection) {
    debugLog('[WebRTC Instrumentation] RTCPeerConnection not available')
    return
  }

  debugLog('[WebRTC Instrumentation] Starting instrumentation...')
  debugLog(
    '[WebRTC Instrumentation] Original RTCPeerConnection:',
    typeof window.RTCPeerConnection
  )

  try {
    // Test if we can create a basic peer connection before patching
    const testConfig: RTCConfiguration = {
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    }
    const testPc = new window.RTCPeerConnection(testConfig)
    testPc.close()
    debugLog('[WebRTC Instrumentation] Basic peer connection test successful')
  } catch (error) {
    debugLog(
      '[WebRTC Instrumentation] Basic peer connection test failed:',
      error
    )
    return
  }

  ;(window as any).__pc_instrumented__ = true
  const Orig = window.RTCPeerConnection

  // Expose registry for cleaners
  ;(window as any).getActivePeerConnections = () => {
    const connections = Array.from(Live)
    debugLog(
      `[WebRTC Instrumentation] getActivePeerConnections called, returning ${connections.length} connections`
    )
    return connections
  }

  // Patch constructor - use a regular function instead of async
  const PatchedRTCPeerConnection = function (
    this: RTCPeerConnection,
    configuration?: RTCConfiguration
  ) {
    debugLog(`[WebRTC Instrumentation] Creating new peer connection...`)

    // Check budget synchronously first (using current connection count)
    const currentCount = Live.size
    const maxConnections = budgetManager.getMaxConnections()

    debugLog(
      `[WebRTC Instrumentation] Current count: ${currentCount}, Max: ${maxConnections}`
    )

    // Allow temporary overrun for batch connection creation (Trystero creates 10-20 connections at once)
    const allowOverrun = currentCount < maxConnections + 50

    if (!allowOverrun) {
      debugLog(
        `[WebRTC Instrumentation] Budget exceeded! Blocking connection creation.`
      )
      throw new Error(
        'WebRTC connection budget exceeded. Cannot create new connection.'
      )
    }

    debugLog(`[WebRTC Instrumentation] Budget allows connection, creating...`)

    // Create the peer connection only if budget allows
    // Use proper constructor call for better browser compatibility
    let pc: RTCPeerConnection
    try {
      pc = new Orig(configuration)
    } catch (error) {
      debugLog(
        `[WebRTC Instrumentation] Failed to create peer connection:`,
        error
      )
      throw error
    }

    // Add to tracking immediately after creation
    Live.add(pc)
    debugLog(
      `[WebRTC Instrumentation] Connection created and tracked. Total: ${Live.size}`
    )

    const remove = () => Live.delete(pc)

    // Auto-remove on terminal states
    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
        remove()
      }
    })

    // Wrap close to ensure proper cleanup
    const origClose = pc.close.bind(pc)
    pc.close = () => {
      try {
        pc.getTransceivers?.().forEach(t => t.stop?.())
      } catch {}

      try {
        pc.ontrack = null
        pc.onicecandidate = null
        pc.ondatachannel = null
      } catch {}

      try {
        if (pc.sctp?.transport) {
          pc.sctp.transport.onstatechange = null
        }
      } catch {}

      origClose()
      remove()
    }

    return pc
  }

  // Preserve static properties and prototype
  try {
    Object.setPrototypeOf(PatchedRTCPeerConnection, Orig)
    PatchedRTCPeerConnection.prototype = Orig.prototype
  } catch (error) {
    debugLog('[WebRTC Instrumentation] Failed to set prototype:', error)
  }

  // Replace the global constructor - wrap in try-catch for Firefox compatibility
  try {
    window.RTCPeerConnection = PatchedRTCPeerConnection as any
    debugLog('[WebRTC Instrumentation] Successfully patched RTCPeerConnection')
  } catch (error) {
    debugLog(
      '[WebRTC Instrumentation] Failed to patch RTCPeerConnection:',
      error
    )
    // Clean up the instrumentation flag if patching fails
    delete (window as any).__pc_instrumented__
  }
}

/**
 * Get the current count of active RTCPeerConnections
 */
export function getActiveConnectionCount(): number {
  return Live.size
}

/**
 * Get all active peer connections (alias for compatibility)
 */
export function getActivePeerConnections(): RTCPeerConnection[] {
  return Array.from(Live)
}

/**
 * Clear the registry of active connections
 * Note: This does not close the connections, only removes them from tracking
 */
export function clearRegistry(): void {
  Live.clear()
}
